import { makeStyles } from '@material-ui/core/styles'
import { coachProfileBandSectionGap, coachProfileSectionHeading } from '@lib/pga-coach/coach-profile-layout'
import {
  getFilterMenuItemSelectedStyles,
  getFilterMenuLeadingStyles,
} from '@components/coaches-search/filter-menu-item.styles'

const useStyles = makeStyles((theme) => ({
  root: {
    scrollMarginTop: 120,
  },
  rootBand: {
    ...coachProfileBandSectionGap(theme, { property: 'marginTop' }),
  },
  heading: {
    ...coachProfileSectionHeading(theme),
    margin: 0,
    marginBottom: theme.spacing(2.5),
  },
  /**
   * Same inline pill row as `/coaches/search`: wrap, 36px pills, circular
   * clear. Left-aligned in the shop column (directory centers its toolbar).
   */
  filterToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  filterPillButton: {
    minHeight: 36,
    height: 36,
    padding: theme.spacing(0.375, 1.25),
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.2,
    color: '#00234B',
    backgroundColor: theme.palette.common.white,
    border: '1px solid #dfe3ea',
    boxShadow: 'none',
    textDecoration: 'none',
    cursor: 'pointer',
    '& .MuiButton-label': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
      marginLeft: 0,
      color: 'inherit',
      '& svg': {
        width: 16,
        height: 16,
      },
    },
    '& .MuiButton-endIcon': {
      marginLeft: theme.spacing(0.5),
      marginRight: 0,
      color: 'inherit',
      '& svg': {
        width: 18,
        height: 18,
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.blue.light25,
      borderColor: theme.palette.blue.main,
      color: theme.palette.blue.main,
      boxShadow: 'none',
      textDecoration: 'none',
    },
    '&.MuiButtonBase-root:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&.MuiButtonBase-root:focus-visible': {
      outline: `2px solid ${theme.palette.secondary.main}`,
      outlineOffset: 2,
    },
  },
  filterPillButtonActive: {
    color: '#3268A7',
    backgroundColor: '#ECF2F9',
    border: '1px solid #3268A7',
    '&:hover': {
      color: '#3268A7',
      backgroundColor: '#ECF2F9',
      borderColor: '#3268A7',
      textDecoration: 'none',
    },
  },
  filterMenuPaper: {
    borderRadius: 12,
    backgroundColor: theme.palette.common.white,
    border: '1px solid rgba(15, 50, 84, 0.08)',
    boxShadow: '0 8px 24px rgba(15, 34, 52, 0.12)',
    minWidth: 260,
    maxWidth: 360,
    // 9 × 40px items + vertical padding so Tags can scroll without
    // clipping a label in half.
    maxHeight: 376,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      maxHeight: 296,
    },
  },
  filterMenuItem: {
    minHeight: 40,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    whiteSpace: 'normal',
    cursor: 'pointer',
    '& .MuiTypography-root': {
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
  },
  filterMenuItemSelected: getFilterMenuItemSelectedStyles(theme),
  ...getFilterMenuLeadingStyles(theme),
  clearButtonWrap: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  iconFilterCircle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    width: 36,
    height: 36,
    minWidth: 36,
    maxWidth: 36,
    minHeight: 36,
    maxHeight: 36,
    padding: 0,
    borderRadius: '50%',
    textTransform: 'none',
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    border: '1px solid #dfe3ea',
    backgroundColor: theme.palette.common.white,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      borderColor: '#cfd6e0',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: '#c7ccd3',
      backgroundColor: theme.palette.common.white,
      borderColor: '#dfe3ea',
    },
    '& .MuiButton-startIcon': {
      margin: 0,
      display: 'inline-flex',
      color: 'inherit',
      '& svg': {
        fontSize: 16,
      },
    },
    '&.MuiButtonBase-root:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&.MuiButtonBase-root:focus-visible': {
      outline: `2px solid ${theme.palette.secondary.main}`,
      outlineOffset: 2,
    },
  },
  iconFilterCircleActive: {
    color: '#3268A7 !important',
    border: '1px solid #3268A7',
    backgroundColor: '#ECF2F9',
    '&:hover': {
      borderColor: '#3268A7',
      backgroundColor: '#ECF2F9',
    },
    '& .MuiButton-startIcon': {
      color: '#3268A7',
    },
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    gap: theme.spacing(1),
  },
  sectionLabel: {
    margin: 0,
  },
  loading: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  filterEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    padding: theme.spacing(2, 0, 1),
  },
  filterEmptyTitle: {
    margin: 0,
    color: theme.palette.secondary.dark,
    fontWeight: 800,
  },
  filterEmptyBody: {
    margin: 0,
    color: theme.palette.grey.dark,
    maxWidth: 420,
  },
  filterEmptyCta: {
    minHeight: 40,
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1, 3),
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.2,
    cursor: 'pointer',
    color: theme.palette.secondary.dark,
    border: `1px solid ${theme.palette.secondary.dark}`,
    backgroundColor: theme.palette.common.white,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.blue.light25,
      boxShadow: 'none',
    },
    '&.MuiButtonBase-root:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&.MuiButtonBase-root:focus-visible': {
      outline: `2px solid ${theme.palette.secondary.main}`,
      outlineOffset: 2,
    },
  },
  /**
   * External Tool card — listing-row photo column plus heading,
   * body, and Open Site. Not an offering row and not a whole-card link.
   */
  externalBannerBelowHeading: {
    marginBottom: theme.spacing(2.5),
  },
  externalBanner: {
    display: 'grid',
    gridTemplateColumns: '220px minmax(0, 1fr)',
    alignItems: 'stretch',
    overflow: 'hidden',
    borderRadius: 12,
    border: '1px solid #dfe3ea',
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  externalBannerImageWrap: {
    position: 'relative',
    minHeight: 176,
    overflow: 'hidden',
    backgroundColor: '#e7eef6',
    [theme.breakpoints.down('sm')]: {
      height: 148,
      minHeight: 148,
    },
  },
  externalBannerImage: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
  },
  externalBannerBody: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5, 3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  externalBannerTitle: {
    margin: 0,
    color: theme.palette.secondary.dark,
  },
  externalBannerCopy: {
    margin: 0,
    color: theme.palette.grey.dark,
  },
}))

export default useStyles
